/**
 * Choose which source a conflicted fact shows — or keep both.
 *
 * Laid out like the redesign's "Two files disagree" check (DOP-623): every
 * value is a card side by side — the figure, the file, where in it in words
 * ("Sheet “Annual Financials”, cell E8") and a link that opens it there —
 * with its own "Use" button. The raw extracted passage is NOT shown: for a
 * spreadsheet it is a row of cell dumps no reader can use; the file, opened
 * at the value, is the evidence. Doppler SUGGESTS and a
 * person decides (DOP-577): the system's pick carries "Doppler suggests" and
 * its reasons in plain words, and nothing is recorded until a person presses
 * a Use button. "Neither is right" (a figure from elsewhere, with where it
 * came from — both readings can be wrong or out of date) and "Keep both and
 * flag it" are real answers too. A
 * fact a person already chose for marks that card "Your choice", so changing
 * it is the same screen.
 *
 * Two disagreeing values often come from ONE file (a model's summary vs its
 * rollup), so each card names the cell or page, and the choice is keyed by the
 * source's anchor, never by the file.
 */

import { ArrowUpRight, FileText, Sparkles } from "lucide-react";
import React, { useId, useState } from "react";

import type {
	DealScreeningFact,
	DealScreeningFactValue,
	DealScreeningSetFactPayload,
	DealScreeningSource,
} from "../../types/dealScreening";
import { BTN, BTN_PRIMARY, INPUT, KICKER, KIND_KICKER } from "./checkStyles";
import { parseEntry, UNIT_HINT } from "./EnterWithSourceCard";
import { formatFactValue, formatIsoDay } from "./dealScreeningFormat";

export interface ConflictChooserProps {
	fact: DealScreeningFact;
	saving: boolean;
	/** The chosen source; `null` keeps both values and leaves the conflict flagged. */
	onChoose: (chosen: DealScreeningSource | null) => void;
	onOpenSource: (source: DealScreeningSource) => void;
	/** Leave without deciding; the button is not shown without it. */
	onCancel?: () => void;
	cancelLabel?: string;
	/** Where the memo writes its questions: "section 8" (DOP-573). */
	questionsIn: string;
	/**
	 * "Neither is right": a figure a person has from elsewhere — newer
	 * accounts, a call — with where it came from. Not offered without it.
	 */
	onEnter?: (fact: DealScreeningFact, payload: DealScreeningSetFactPayload) => void;
}

interface Option {
	value: DealScreeningFactValue;
	unit: string;
	source: DealScreeningSource;
}

/** Where in the file, in words: "Sheet “Annual Financials”, cell E8", "Page 4". */
export function placeInFile(source: DealScreeningSource): string | null {
	if (source.kind === "web") return "Web page";
	if (source.sheet && source.cell) return `Sheet “${source.sheet.replace(/_/g, " ")}”, cell ${source.cell}`;
	if (source.page !== null) return `Page ${source.page}${source.ocr ? " (scanned)" : ""}`;
	return null;
}

/** "The two figures are $1.57mm (4%) apart." — for two numbers in one unit. */
function howFarApart(options: Option[]): string | null {
	if (options.length !== 2) return null;
	const [a, b] = options;
	if (typeof a.value !== "number" || typeof b.value !== "number" || a.unit !== b.unit) return null;
	if (a.unit === "pct" || a.unit === "text") return null;
	const gap = Math.abs(a.value - b.value);
	const base = Math.max(Math.abs(a.value), Math.abs(b.value));
	if (gap === 0 || base === 0) return null;
	const share = (gap / base) * 100;
	return `The two figures are ${formatFactValue(gap, a.unit)} (${
		share < 1 ? share.toFixed(1) : Math.round(share)
	}%) apart.`;
}

const ConflictChooser: React.FC<ConflictChooserProps> = ({
	fact,
	saving,
	onChoose,
	onOpenSource,
	onCancel,
	cancelLabel = "Cancel",
	questionsIn,
	onEnter,
}) => {
	const [entering, setEntering] = useState(false);
	const [value, setValue] = useState("");
	const [note, setNote] = useState("");
	const valueId = useId();
	const noteId = useId();
	const parsed = parseEntry(value, fact.unit);
	const entryReady = parsed !== null && note.trim() !== "" && !saving;
	const saveEntry = () => {
		if (entryReady && onEnter) onEnter(fact, { value: parsed, note: note.trim(), period: fact.period });
	};
	const options: Option[] = [
		...(fact.source ? [{ value: fact.value, unit: fact.unit, source: fact.source }] : []),
		...fact.alternates,
	];
	const recommended = fact.recommended ?? null;
	const chosenAnchor = fact.conflict === "resolved" ? (fact.source?.anchor ?? null) : null;
	const name = fact.period ? `${fact.label} ${fact.period}` : fact.label;
	const apart = howFarApart(options);

	return (
		<form
			onSubmit={(event) => {
				event.preventDefault();
				if (entering) saveEntry();
			}}
			aria-label="Choose a source"
			className="flex flex-col gap-4"
		>
			<div>
				<p className={`${KICKER} ${KIND_KICKER.conflict.color}`}>
					{options.length === 2 ? "Two readings disagree" : `${options.length} readings disagree`}
				</p>
				<h2 className="mt-1 text-[22px] font-semibold leading-tight tracking-[-0.01em] text-[#111111]">
					Which {name} should the memo use?
				</h2>
				{apart && <p className="mt-1 text-sm text-[#5B6472]">{apart}</p>}
			</div>

			<div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(240px,1fr))]">
				{options.map((option, index) => {
					const label = formatFactValue(option.value, option.unit);
					const suggested = recommended?.anchor === option.source.anchor;
					const yours = chosenAnchor === option.source.anchor;
					const place = placeInFile(option.source);
					return (
						<div
							key={option.source.anchor}
							data-testid="conflict-option"
							className={`flex flex-col gap-3 rounded-xl bg-white p-4 ${
								suggested ? "border-2 border-[#004C4B]" : "border border-[#e5e7eb]"
							}`}
						>
							<div className="flex min-h-[22px] flex-wrap items-center gap-2">
								{suggested && (
									<span
										className="inline-flex h-[22px] items-center gap-1.5 rounded-full bg-[#004C4B] px-2 text-[11px] font-semibold text-white"
										data-testid="conflict-recommended"
									>
										<Sparkles className="h-3 w-3" aria-hidden />
										Doppler suggests
									</span>
								)}
								{yours && (
									<span className="inline-flex h-[22px] items-center rounded-full bg-[#ecfdf3] px-2 text-[11px] font-semibold text-[#067647]">
										Your choice
									</span>
								)}
								{!yours && index === 0 && (
									<span className="text-xs text-[#6B7280]">In the memo now</span>
								)}
							</div>
							<p className="text-2xl font-bold leading-none tabular-nums text-[#111111]">
								{label}
							</p>
							<div className="text-[13px] leading-snug">
								<p className="flex min-w-0 items-center gap-1.5 text-[#3f4247]">
									<FileText className="h-3.5 w-3.5 shrink-0 text-[#6B7280]" aria-hidden />
									<span className="truncate" title={option.source.title || option.source.filename}>
										{option.source.title || option.source.filename}
									</span>
								</p>
								<p className="mt-0.5 pl-5 text-[12.5px] text-[#6B7280]">
									{[
										place,
										option.source.doc_date ? `dated ${formatIsoDay(option.source.doc_date)}` : null,
									]
										.filter(Boolean)
										.join(" · ")}
								</p>
								<button
									type="button"
									onClick={() => onOpenSource(option.source)}
									className="mt-1.5 inline-flex items-center gap-1 pl-5 text-[13px] font-semibold text-[#004C4B] hover:underline"
								>
									See it in the file
									<ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
								</button>
							</div>
							{suggested && recommended.reasons.length > 0 && (
								<p className="rounded-lg bg-[#f6fbfb] px-3 py-2 text-[12.5px] leading-snug text-[#004C4B]">
									<b>Why:</b> {recommended.reasons.join(" · ")}
								</p>
							)}
							<button
								type="button"
								aria-label={`Use ${label}`}
								disabled={saving}
								onClick={() => onChoose(option.source)}
								className={`${suggested ? BTN_PRIMARY : BTN} mt-auto h-10`}
							>
								{saving ? "Saving…" : `Use ${label}`}
							</button>
						</div>
					);
				})}
			</div>

			<div className="divide-y divide-dashed divide-[#d1d5db] rounded-xl border border-dashed border-[#d1d5db]">
				{onEnter && (
					<div className="px-4 py-3">
						<div className="flex flex-wrap items-center gap-3">
							<div className="min-w-0 flex-1">
								<p className="text-sm font-semibold text-[#111111]">Neither is right?</p>
								<p className="text-[12.5px] text-[#5B6472]">
									Have the figure from another source? Enter it with where it came from; the
									memo marks it as entered by you.
								</p>
							</div>
							{!entering && (
								<button
									type="button"
									className={`${BTN} h-10`}
									disabled={saving}
									onClick={() => setEntering(true)}
								>
									Enter the right figure
								</button>
							)}
						</div>
						{entering && (
							<div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
								<div className="flex flex-col gap-1.5">
									<label htmlFor={valueId} className="text-[12.5px] font-semibold text-[#3f4247]">
										{name}
									</label>
									<input
										id={valueId}
										value={value}
										onChange={(event) => setValue(event.target.value)}
										inputMode="decimal"
										className={`${INPUT} h-10`}
										autoFocus
									/>
									{UNIT_HINT[fact.unit] && (
										<p className="text-xs text-[#6B7280]">{UNIT_HINT[fact.unit]}</p>
									)}
								</div>
								<div className="flex flex-col gap-1.5">
									<label htmlFor={noteId} className="text-[12.5px] font-semibold text-[#3f4247]">
										Where it's from
									</label>
									<input
										id={noteId}
										value={note}
										onChange={(event) => setNote(event.target.value)}
										placeholder="e.g. Q3 management accounts, received Oct 2"
										className={`${INPUT} h-10`}
									/>
								</div>
								<div className="flex gap-2.5 sm:col-span-2 sm:justify-end">
									<button
										type="button"
										className={`${BTN} h-10`}
										disabled={saving}
										onClick={() => {
											setEntering(false);
											setValue("");
											setNote("");
										}}
									>
										Cancel
									</button>
									<button type="submit" className={`${BTN_PRIMARY} h-10`} disabled={!entryReady}>
										{saving ? "Saving…" : "Use this figure"}
									</button>
								</div>
							</div>
						)}
					</div>
				)}
				<div className="flex flex-wrap items-center gap-3 px-4 py-3">
					<div className="min-w-0 flex-1">
						<p className="text-sm font-semibold text-[#111111]">Not sure which is right?</p>
						<p className="text-[12.5px] text-[#5B6472]">
							Keep both: the memo shows the first figure, flags it, and asks the company in{" "}
							{questionsIn}.
						</p>
					</div>
					<button type="button" className={`${BTN} h-10`} disabled={saving} onClick={() => onChoose(null)}>
						Keep both and flag it
					</button>
					{onCancel && (
						<button type="button" className={`${BTN} h-10`} disabled={saving} onClick={onCancel}>
							{cancelLabel}
						</button>
					)}
				</div>
			</div>
		</form>
	);
};

export default ConflictChooser;
